import {useUpmPlantContext} from '@hconnect/upm'
import {Moment} from 'moment-timezone'
import {useCallback} from 'react'

import {getStoragesFromHistory, isHistoryStorageWithMaterialGuard} from '../selectors'
import {MaterialType, Status} from '../types'
import type {MaterialStoragesHistory} from '../types'

import {useMaterialStorageHistory} from './useMaterialStorageHistory'

type MomentRange = [start: Moment, end: Moment]

interface SelectorParams {
  storageHistory: MaterialStoragesHistory
  date: Moment
  materialType?: MaterialType
  hideDeleted: boolean
}

const getStorages = ({storageHistory, date, materialType, hideDeleted}: SelectorParams) => {
  const storagesFromHistory = getStoragesFromHistory(storageHistory, date)
  const storagesWithMaterial = storagesFromHistory
    .filter(isHistoryStorageWithMaterialGuard)
    .filter((storage) => (materialType ? storage.materialType === materialType : true))
    .filter((storage) => (hideDeleted ? storage.status !== Status.Deleted : true))

  return [...new Set(storagesWithMaterial?.map(({materialId}) => materialId) ?? [])]
}

interface StoredMaterialIdsQueryParams {
  materialType?: MaterialType
  timeFrame: MomentRange
  date?: Moment
  hideDeleted?: boolean
}

export const useStoredMaterialIdsFromHistoryQuery = ({
  materialType,
  timeFrame: [from, to],
  date,
  hideDeleted = true
}: StoredMaterialIdsQueryParams) => {
  const {plantId: plantCode} = useUpmPlantContext()

  const selector = useCallback(
    (data: MaterialStoragesHistory) =>
      getStorages({storageHistory: data, date: date ?? to, materialType, hideDeleted}),
    [date, to, materialType, hideDeleted]
  )
  return useMaterialStorageHistory({
    plantId: plantCode,
    from: from.toISOString(),
    to: to.toISOString(),
    options: {select: selector, throwOnError: true}
  })
}
